import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  ReferenceLine,
} from "recharts";
import { useWavesurfer } from "@wavesurfer/react";
//@ts-ignore
import Spectrogram from "wavesurfer.js/dist/plugins/spectrogram.esm.js";
import { api_key, authEmployeeID, sdkURL } from "../utils/constants";
import Failure from "../assets/images/Failure.webp";
import { ReactComponent as Play } from "../assets/icons/play.svg";
import { ReactComponent as Pause } from "../assets/icons/pause.svg";
import LoadingScreen from "../components/LoadingScreen";
import BackArrow from "../assets/images/back-arrow.svg";

const ScoreData = ({ isGood, fev1fvcRatio }) => {
  const navigate = useNavigate();

  return (
    <>
      <div className="flex items-center tab-screen">
        <div className="flex items-center mb-8 w-full relative z-50">
          <Link
            to="https://gitex-demo.careplix.com/UnityHealthExperienceCenter"
            className="w-9 h-9 bg-[#2D2D2D] rounded-full flex flex-row justify-center items-center"
          >
            <img src={BackArrow} alt="" />
          </Link>
          <h2 className="text-xl lg:text-2xl md:text-2xl font-medium text-white ml-6">
            Assessment Result
          </h2>
        </div>
      </div>
      <div className="circle-blur w-96 h-96 mx-auto absolute left-0 right-0 top-20 blur-[90px]"></div>
      <h1 className="mt-0 text-2xl relative z-50 text-center mb-5">
        {"Assessment indicates "}
        <span
          className="font-bold"
          style={{ color: isGood ? "#59D341" : "#FF2B2B" }}
        >
          {isGood && "no "}presence
        </span>
        {" of obstructive airway disease (OAD)"}
      </h1>
      <div
        className={`mt-4 p-4 relative z-50 rounded-2xl ${
          isGood ? "bg-success/35" : "bg-error/25"
        } text-sm`}
      >
        <h2 className="font-semibold">
          {isGood && "No "}Obstructive Airway Disease (OAD) Risk Found:
        </h2>
        <p className="mt-1 font-light">
          {"Your assessment shows "}
          <span className="font-semibold">{isGood && "no "}risk</span>
          {/* {` of having ${
            fev1fvcRatio < 0.65 ? "COPD" : "Asthma/COPD"
          } at this time.`} */}
          {` of having Asthma/COPD at this time.`}
        </p>
      </div>

      <div className="mt-5 p-6 rounded-[20px] bg-[#1D1D1D]/70 relative z-50">
        <h2 className="font-light text-secondary">
          Predicted FEV1/FVC:{" "}
          <span className="font-semibold text-white">{fev1fvcRatio}</span>
        </h2>
        <h3 className="mt-3 text-xs font-semibold">What is FEV1/FVC Value?</h3>
        <p className="mt-2 text-xxs relative z-50">
          The FEV1/FVC ratio is a key lung function indicator that compares the
          amount of air you can forcefully exhale in one second (FEV1) to the
          total amount of air you can exhale after taking a deep breath (FVC).
          <br />
          It helps assess how well your lungs are working and is commonly used
          to detect airflow obstruction in conditions like Asthma or COPD.
          <br />
          <br />✅ Normal Range: Typically above 70-80% (varies by age)
          <br />
          <br />✅ Lower Values: May indicate obstructive lung disease
        </p>
        <div className="circle-blur w-32 h-32 mx-auto absolute right-0 bottom-0 blur-[50px]"></div>
      </div>

      <div className="mt-6 p-6 rounded-[20px] bg-[#364560] text-wgite relative z-50">
        <h3 className="text-sm font-semibold text-secondary">
          For Investigational Use Only :
        </h3>
        <p className="mt-2 text-xxs">
          Careplix Pulmo is not a medical device and should not be used as a
          substitute for clinical judgment by a health care professional.
          CarePlix Pulmo is intended to improve your awareness of your lung
          obstruction health and not intended to diagnose, treat, mitigate or
          prevent any disease, symptom, disorder or abnormal physical condition.
          Contact your health-care provider immediately if you suspect that you
          have a medical problem. Statements regarding clinical data have not
          been evaluated by the US FDA and are only intended for self-awareness
          and general wellness purposes only.
        </p>
      </div>
    </>
  );
};

const AudioSample = ({ title, audioURL, onPoorQuality }) => {
  const waveformContainerRef = useRef(null);
  const { isReady, isPlaying, wavesurfer } = useWavesurfer({
    container: waveformContainerRef,
    url: audioURL,
    height: 40,
    interact: false,
    waveColor: "#fff",
    progressColor: "#808080",
    cursorColor: "#fff",
    cursorWidth: 2,
    barGap: 1,
    barWidth: 2,
    barRadius: 999,
  });
  const [quality, setQuality] = useState("Good");

  useEffect(() => {
    if (wavesurfer) {
      wavesurfer.registerPlugin(
        Spectrogram.create({ height: 60, splitChannels: false })
      );
      wavesurfer.on("decode", (duration) => {
        const decodedData = wavesurfer.getDecodedData();
        if (decodedData) {
          const audioData = decodedData.getChannelData(0);
          const minValue = 0.025;
          const minSilenceDuration = 0.1;
          const mergeDuration = 0.2;
          const scale = duration / audioData.length;
          const silentRegions = [];
          let start = 0;
          let end = 0;
          let isSilent = false;
          for (let i = 0; i < audioData.length; i++) {
            if (audioData[i] < minValue) {
              if (!isSilent) {
                start = i;
                isSilent = true;
              }
            } else if (isSilent) {
              end = i;
              isSilent = false;
              if (scale * (end - start) > minSilenceDuration) {
                silentRegions.push({
                  start: scale * start,
                  end: scale * end,
                });
              }
            }
          }
          const mergedRegions = [];
          let lastRegion = null;
          for (let i = 0; i < silentRegions.length; i++) {
            if (
              lastRegion &&
              silentRegions[i].start - lastRegion.end < mergeDuration
            ) {
              lastRegion.end = silentRegions[i].end;
            } else {
              lastRegion = silentRegions[i];
              mergedRegions.push(lastRegion);
            }
          }
          const regions = [];
          let lastEnd = 0;
          for (let i = 0; i < mergedRegions.length; i++) {
            regions.push({
              start: lastEnd,
              end: mergedRegions[i].start,
            });
            lastEnd = mergedRegions[i].end;
          }
          if (title === "Cough") {
            if (regions.length >= 3 && regions.length <= 6) setQuality("Good");
            else {
              setQuality("Poor");
              onPoorQuality();
            }
          } else if (title === "Voice") {
            if (regions.length !== 1) {
              setQuality("Poor");
              onPoorQuality();
            } else setQuality("Good");
          }
        }
      });
    }
    return () => {
      wavesurfer?.unAll?.();
    };
  }, [title, wavesurfer, onPoorQuality]);

  return (
    <div className="mt-4 space-y-1 bg-white">
      <h2 className="text-primary font-medium">Recorded {title} Sample</h2>
      <div className="px-4 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D] text-secondary">
        <button
          className="h-10 w-10"
          type="button"
          disabled={!isReady}
          onClick={() => {
            wavesurfer?.playPause?.();
          }}
        >
          <span className="sr-only">Play/Pause</span>
          {isPlaying ? <Pause /> : <Play />}
        </button>
        <div
          className="grow border-l-2 border-secondary"
          ref={waveformContainerRef}
        />
      </div>
      {quality === "Poor" && (
        <p className="text-primary text-xxs">
          <span className="text-error">
            We detected that the audio sample might not be accurate.
          </span>
          <br />
          <span>
            The recording doesn't seem to have the expected characteristics.
          </span>
          &nbsp;
          {title === "Voice" ? (
            <span>
              Please ensure that you're using the correct pronunciation and
              holding the sound consistently.
            </span>
          ) : (
            title === "Cough" && (
              <span>
                The recording doesn't seem to have the expected characteristics.
                Please ensure you're coughing naturally and directly into the
                microphone.
              </span>
            )
          )}
        </p>
      )}
    </div>
  );
};

const PulmonaryResult = () => {
  const { state } = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [audioQualityLevel, setAudioQualityLevel] = useState(3);
  const [result, setResult] = useState({
    isOADScoreGood: true,
    fev1fvcRatio: 0,
    prq: 0,
    hrTimeData: { data: [], min: 0, max: 0 },
    rrTimeData: { data: [], min: 0, max: 0 },
  });

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setError("");
      setAudioQualityLevel(3);
      setLoading(true);
      try {
        const resp = await fetch(sdkURL("/pulmo/generate-audio-score"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            api_key,
            employee_id: authEmployeeID,
            scan_id: state?.scanID ?? "",
          }),
          signal: controller.signal,
        });
        const respJSON = await resp.json();
        if (respJSON?.statusCode?.toString().startsWith("2")) {
          const hrTimeArray = respJSON?.heartrate_time ?? [];
          let hrMin = 0;
          let hrMax = 0;
          const hrData = [];
          for (let i = 0; i < hrTimeArray.length; i++) {
            if (hrTimeArray[i] < hrTimeArray[hrMin]) hrMin = i;
            if (hrTimeArray[i] >= hrTimeArray[hrMax]) hrMax = i;
            hrData.push({ time: 5 * (i + 1), value: hrTimeArray[i] });
          }
          const rrTimeArray = respJSON?.resp_rate_time ?? [];
          let rrMin = 0;
          let rrMax = 0;
          const rrData = [];
          for (let i = 0; i < rrTimeArray.length; i++) {
            if (rrTimeArray[i] < rrTimeArray[rrMin]) rrMin = i;
            if (rrTimeArray[i] >= rrTimeArray[rrMax]) rrMax = i;
            rrData.push({ time: 5 * (i + 1), value: rrTimeArray[i] });
          }
          setResult({
            isOADScoreGood: respJSON?.oad === false,
            fev1fvcRatio: respJSON?.["fev1/fvc"] || 0,
            prq: respJSON?.prq || 0,
            hrTimeData: { data: hrData, min: hrMin, max: hrMax },
            rrTimeData: { data: rrData, min: rrMin, max: rrMax },
          });
        } else
          throw new Error(respJSON?.message ?? "Error in Fetching Result.");
      } catch (err) {
        console.error(err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      controller.abort();
    };
  }, [state]);

  return loading ? (
    <LoadingScreen style={{ minHeight: "87.5dvh" }} />
  ) : error.length > 0 ? (
    <section className="fixed top-0 bottom-0 left-0 right-0 px-8 py-12 flex flex-col items-center justify-center bg-black text-center">
      <img className="shrink-0 h-48" src={Failure} alt="Failure..." />
      <p className="mt-8 text-secondary text-xl font-bold">
        Sorry, we failed to get Results
      </p>
      <p className="mt-4 max-h-40 overflow-auto whitespace-pre-line text-white font-light">
        {error}
      </p>
      <Link
        //@ts-ignore
        to={-2}
        className="mt-8 w-60 px-5 py-2.5 rounded-full bg-white text-primary font-medium"
      >
        Retry
      </Link>
    </section>
  ) : (
    <section className="p-0 pt-2 ">
      <div className="px-6 !mb-10 tab-screen">
        <ScoreData
          isGood={result.isOADScoreGood}
          fev1fvcRatio={result.fev1fvcRatio}
        />

        {audioQualityLevel < 3 && (
          <div className="mt-6 p-6 flex items-center space-x-4 rounded-[20px] bg-[#f8b62d]">
            <svg
              className="shrink-0 w-8"
              viewBox="0 0 122.88 111.54"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="#000"
                d="M2.35,84.42,45.28,10.2l.17-.27h0A23,23,0,0,1,52.5,2.69,17,17,0,0,1,61.57,0a16.7,16.7,0,0,1,9.11,2.69,22.79,22.79,0,0,1,7,7.26q.19.32.36.63l42.23,73.34.24.44h0a22.48,22.48,0,0,1,2.37,10.19,17.63,17.63,0,0,1-2.17,8.35,15.94,15.94,0,0,1-6.93,6.6c-.19.1-.39.18-.58.26a21.19,21.19,0,0,1-9.11,1.75v0H17.61c-.22,0-.44,0-.65,0a18.07,18.07,0,0,1-6.2-1.15A16.42,16.42,0,0,1,3,104.24a17.53,17.53,0,0,1-3-9.57,23,23,0,0,1,1.57-8.74,7.66,7.66,0,0,1,.77-1.51Z"
              />
              <path
                fill="#f8b62d"
                d="M9,88.75,52.12,14.16c5.24-8.25,13.54-8.46,18.87,0l42.43,73.69c3.39,6.81,1.71,16-9.33,15.77H17.61C10.35,103.8,5.67,97.43,9,88.75Z"
              />
              <path
                fill="#000"
                d="M57.57,83.78A5.53,5.53,0,0,1,61,82.2a5.6,5.6,0,0,1,2.4.36,5.7,5.7,0,0,1,2,1.3,5.56,5.56,0,0,1,1.54,5,6.23,6.23,0,0,1-.42,1.35,5.57,5.57,0,0,1-5.22,3.26,5.72,5.72,0,0,1-2.27-.53A5.51,5.51,0,0,1,56.28,90a5.18,5.18,0,0,1-.36-1.27,5.83,5.83,0,0,1-.06-1.31h0a6.53,6.53,0,0,1,.57-2,4.7,4.7,0,0,1,1.14-1.56Zm8.15-10.24c-.19,4.79-8.31,4.8-8.49,0-.82-8.21-2.92-29.34-2.86-37.05.07-2.38,2-3.79,4.56-4.33a12.83,12.83,0,0,1,5,0c2.61.56,4.65,2,4.65,4.44v.24L65.72,73.54Z"
              />
            </svg>
            <p className="text-xxs">
              This assessment result is generated based on your finger scan,
              'A/' sound sample, and questionnaire responses. The{" "}
              {audioQualityLevel === 2
                ? "Voice"
                : audioQualityLevel === 1
                ? "Cough"
                : "Voice & Cough"}{" "}
              sample recording did not meet the required characteristics and was
              excluded from the overall assessment computation.
            </p>
          </div>
        )}
      </div>
      <div className=" bg-white px-6 pt-3 pb-20">
        <div className="tab-screen grid grid-cols-2 gap-4">
          <AudioSample
            title="Voice"
            audioURL={state?.aVowelAudioB64Data ?? ""}
            onPoorQuality={() => setAudioQualityLevel((s) => s - 1)}
          />
          <AudioSample
            title="Cough"
            audioURL={state?.coughAudioB64Data ?? ""}
            onPoorQuality={() => setAudioQualityLevel((s) => s - 2)}
          />

          <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
            <h3 className="mb-4 text-lg">Heart Rate</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={result.hrTimeData.data}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient
                    id="chart-bar-gradient-hr"
                    x1="0%"
                    y1="50%"
                    x2="100%"
                    y2="50%"
                  >
                    <stop offset="25%" stopColor="#0EA573" />
                    <stop offset="75%" stopColor="#063BC4" />
                  </linearGradient>
                </defs>
                <XAxis
                  type="number"
                  dataKey="time"
                  allowDataOverflow={false}
                  domain={[0, 35]}
                  ticks={[5, 10, 15, 20, 25, 30]}
                  tickLine={false}
                  tickFormatter={(v) => `${v} sec`}
                  fontSize={10}
                  stroke="#fff"
                  strokeOpacity={0.75}
                  strokeWidth={1}
                />
                <YAxis
                  type="number"
                  dataKey="value"
                  domain={["dataMin - 8", "dataMax + 8"]}
                  tick={false}
                  tickMargin={0}
                  stroke="#fff"
                  strokeOpacity={0.75}
                  strokeWidth={1}
                  width={1}
                />
                <Line
                  type="monotone"
                  strokeWidth={6}
                  stroke="url(#chart-bar-gradient-hr)"
                  dataKey="value"
                  dot={false}
                />
                <ReferenceLine
                  x={10}
                  isFront={true}
                  stroke="#fff"
                  strokeOpacity={0.5}
                  strokeDasharray="3 3"
                />
                <ReferenceLine
                  x={25}
                  isFront={true}
                  stroke="#fff"
                  strokeOpacity={0.5}
                  strokeDasharray="3 3"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-1 flex justify-between">
              <div>
                <h4 className="text-secondary text-sm font-semibold">
                  min HR:
                </h4>
                <p>
                  <span className="font-semibold">
                    {result.hrTimeData.data[result.hrTimeData.min]?.value ??
                      "--"}
                  </span>
                  &nbsp;
                  <span className="text-xs">bpm</span>
                </p>
                <p className="text-xxs font-light">
                  ⏱️{" "}
                  {result.hrTimeData.data[result.hrTimeData.min]?.time ?? "--"}{" "}
                  seconds
                </p>
              </div>
              <div>
                <h4 className="text-secondary text-sm font-semibold">
                  max HR:
                </h4>
                <p>
                  <span className="font-semibold">
                    {result.hrTimeData.data[result.hrTimeData.max]?.value ??
                      "--"}
                  </span>
                  &nbsp;
                  <span className="text-xs">bpm</span>
                </p>
                <p className="text-xxs font-light">
                  ⏱️{" "}
                  {result.hrTimeData.data[result.hrTimeData.max]?.time ?? "--"}{" "}
                  seconds
                </p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-6 rounded-[20px] bg-[#1D1D1D] text-white">
            <h3 className="mb-4 text-lg">Respiration Rate</h3>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart
                data={result.rrTimeData.data}
                margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              >
                <defs>
                  <linearGradient
                    id="chart-bar-gradient-rr"
                    x1="0%"
                    y1="50%"
                    x2="100%"
                    y2="50%"
                  >
                    <stop offset="25%" stopColor="#063BC4" />
                    <stop offset="75%" stopColor="#0EA573" />
                  </linearGradient>
                </defs>
                <XAxis
                  type="number"
                  dataKey="time"
                  allowDataOverflow={false}
                  domain={[0, 35]}
                  ticks={[5, 10, 15, 20, 25, 30]}
                  tickLine={false}
                  tickFormatter={(v) => `${v} sec`}
                  fontSize={10}
                  stroke="#fff"
                  strokeOpacity={0.75}
                  strokeWidth={1}
                />
                <YAxis
                  type="number"
                  dataKey="value"
                  domain={["dataMin - 2", "dataMax + 2"]}
                  tick={false}
                  tickMargin={0}
                  stroke="#fff"
                  strokeOpacity={0.75}
                  strokeWidth={1}
                  width={1}
                />
                <Line
                  type="monotone"
                  strokeWidth={6}
                  stroke="url(#chart-bar-gradient-rr)"
                  dataKey="value"
                  dot={false}
                />
                <ReferenceLine
                  x={10}
                  isFront={true}
                  stroke="#fff"
                  strokeOpacity={0.5}
                  strokeDasharray="3 3"
                />
                <ReferenceLine
                  x={25}
                  isFront={true}
                  stroke="#fff"
                  strokeOpacity={0.5}
                  strokeDasharray="3 3"
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="mt-1 flex justify-between">
              <div>
                <h4 className="text-secondary text-sm font-semibold">
                  min RR:
                </h4>
                <p>
                  <span className="font-semibold">
                    {result.rrTimeData.data[result.rrTimeData.min]?.value ??
                      "--"}
                  </span>
                  &nbsp;
                  <span className="text-xs">breathes/min</span>
                </p>
                <p className="text-xxs font-light">
                  ⏱️{" "}
                  {result.rrTimeData.data[result.rrTimeData.min]?.time ?? "--"}{" "}
                  seconds
                </p>
              </div>
              <div>
                <h4 className="text-secondary text-sm font-semibold">
                  max RR:
                </h4>
                <p>
                  <span className="font-semibold">
                    {result.rrTimeData.data[result.rrTimeData.max]?.value ??
                      "--"}
                  </span>
                  &nbsp;
                  <span className="text-xs">breathes/min</span>
                </p>
                <p className="text-xxs font-light">
                  ⏱️{" "}
                  {result.rrTimeData.data[result.rrTimeData.max]?.time ?? "--"}{" "}
                  seconds
                </p>
              </div>
            </div>
          </div>
          <div className="col-span-full">
            <div className="mt-4 px-6 py-4 flex items-center justify-between space-x-4 rounded-[20px] bg-[#1D1D1D]">
              <h3 className="text-white font-semibold">
                Pulse-Respiration Quotient (PRQ)
              </h3>
              <p className="text-secondary text-2xl font-semibold">
                {result.prq || "--"}
              </p>
            </div>
            <Link
              to={-2}
              className="block mt-8 px-6 py-3 w-1/2 mx-auto rounded-lg outline-none border border-primary/80 bg-white text-primary text-center text-sm font-light"
            >
              Back
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default PulmonaryResult;
